import { useEffect, useMemo, useState } from "react";
import {
  qualifiedToolName,
  type CatalogProvider,
  type Connection,
  type ConnectionTool,
} from "../types";
import { matchesKind, type ToolKind } from "../toolSelection";
import ToolKindFilter from "./ToolKindFilter";

interface Props {
  provider: CatalogProvider;
  conn: Connection;
  slug: string;
  /** The tools this account can actually offer -- declared or discovered. */
  tools: ConnectionTool[];
  /** null = every tool this account can offer is exposed. */
  allowList: string[] | null;
  enabled: boolean;
  connected: boolean;
  /** Set for a team connection: the selection here is shared, so say with whom. */
  teamName: string | null;
  busy: boolean;
  /**
   * Switch some of this account's tools on or off. Takes a list rather than a name because
   * Select all is one decision and must be one write: the allow-list is a single array, so
   * N sequential calls would each compute their answer from the array the last one wrote
   * and the listener has not necessarily delivered yet.
   */
  onToggleTools: (toolNames: string[], on: boolean) => void;
  onClose: () => void;
}

/**
 * The tool selection for one account, as a dialog.
 *
 * It is a dialog rather than a panel that grows inside the card because the two things are
 * read at different moments. The card answers "is this account on, and roughly how much of
 * it?" at a glance, across the whole grid; the tool list is a decision about one account,
 * taken one at a time. Expanding it in place pushed every other card down the page --
 * re-laying out the answer to the first question in order to ask the second -- and a long
 * list (a Generic MCP server can advertise dozens) turned the grid into a scroll.
 *
 * Toggling writes straight through: there is no Save, so there is nothing to lose by
 * closing, and the card behind it updates as you go.
 */
export default function ToolsDialog({
  provider,
  conn,
  slug,
  tools,
  allowList,
  enabled,
  connected,
  teamName,
  busy,
  onToggleTools,
  onClose,
}: Props) {
  const [kind, setKind] = useState<ToolKind>("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isOn = (tool: ConnectionTool) => (allowList ? allowList.includes(tool.name) : true);

  const exposed = tools.filter(isOn).length;

  const needle = query.trim().toLowerCase();

  /**
   * What the typed filter leaves. Name *and* description, because a tool name is written
   * for a model: `search_code` does not contain "find". This is the same compromise the
   * /tools page makes without the AI half -- one account's list is short enough that
   * matching the description is usually enough to find the tool you meant.
   *
   * Kept apart from `shown` so the read/write counts describe the list they are about to
   * narrow: "Write 1" of three search results is the useful reading, "Write 1" of every
   * tool on the account is noise.
   */
  const matched = useMemo(() => {
    if (!needle) return tools;
    // The qualified name rather than the bare one, because that is the name on screen and
    // the name a user pastes back in from their MCP client: typing `github__list` should
    // find `github__list_issues`, not nothing.
    return tools.filter((t) =>
      `${qualifiedToolName(slug, t.name)} ${t.title} ${t.description}`
        .toLowerCase()
        .includes(needle)
    );
  }, [tools, needle, slug]);

  /** What both narrowings leave -- and therefore what Select all means right now. */
  const shown = matched.filter((t) => matchesKind(t, kind));
  const shownExposed = shown.filter(isOn).length;
  const editable = connected && enabled && !busy;

  /** Whether the list on screen is a subset -- which is what "these" rather than "all" means. */
  const narrowed = kind !== "all" || needle !== "";

  /** Why the list came out empty: which of the two narrowings did it, in the user's terms. */
  function emptyReason(): string {
    if (matched.length === 0) return "Nothing here matches that.";
    if (needle) {
      return kind === "read"
        ? "Nothing matching that is read-only."
        : "Nothing matching that can write.";
    }
    return kind === "read"
      ? "Every tool on this account can change something."
      : "Every tool on this account is read-only.";
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Tools for ${conn.accountLabel}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-head">
          <div>
            <h2>
              {conn.accountLabel} · {provider.name}
            </h2>
            <p className="modal-sub">
              {enabled && connected
                ? `${exposed} of ${tools.length} exposed to your MCP client as ${slug}__*`
                : `${tools.length} tool${tools.length === 1 ? "" : "s"}, none exposed`}
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="modal-body">
          {/* A team connection has one tool selection for the whole team, so a change
              here is not a private one -- worth a line before the first click. */}
          {teamName && (
            <p className="tools-hint">
              This account is shared: switching tools on or off here changes them for
              everyone in {teamName}.
            </p>
          )}

          {!connected ? (
            <p className="tools-hint">
              This account needs reconnecting before its tools can be exposed.
            </p>
          ) : (
            !enabled && (
              <p className="tools-hint">Switch this account on to expose any of these.</p>
            )
          )}

          {/* Only a discovered list can be empty: a remote MCP server is entitled to
              advertise no tools at all, and the account is still perfectly connected. */}
          {tools.length === 0 && (
            <p className="tools-hint">
              This server advertises no tools. Refresh the account if you expect it to.
            </p>
          )}

          {/* Narrow the list, then act on exactly what it leaves. Typing is the other
              half of the same move the bar makes: on an account with thirty tools, finding
              the one you came here for should not be a scroll, and "expose the ones that
              only read" is a single decision the user should not have to spell out thirty
              times.

              A row of its own above the bar, because it narrows the list the bar acts on
              -- and because sharing that line with a segmented control and two verbs would
              leave room to type about four characters. */}
          {tools.length > 0 && (
            <div className="tools-search">
              <input
                className="search-input"
                value={query}
                placeholder="Filter tools by name or description…"
                aria-label="Filter tools by name"
                spellCheck={false}
                autoComplete="off"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          )}

          {tools.length > 0 && (
            <div className="tools-bar">
              {/* Counted over what the typed filter left, so the control answers "how much
                  of what I just found can write?" rather than a question about the account
                  the header has already answered. */}
              <ToolKindFilter tools={matched} value={kind} onChange={setKind} />

              <span className="search-actions">
                <button
                  className="btn btn-subtle btn-sm"
                  disabled={!editable || shown.length === 0 || shownExposed === shown.length}
                  onClick={() => onToggleTools(shown.map((t) => t.name), true)}
                >
                  Select {narrowed ? "these" : "all"}
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={!editable || shownExposed === 0}
                  onClick={() => onToggleTools(shown.map((t) => t.name), false)}
                >
                  Clear{narrowed ? " these" : ""}
                </button>
              </span>
            </div>
          )}

          {/* Says something true about the account rather than just "nothing here": an
              account with no write tools is a fact worth learning from the filter, and it
              is a different answer from a word that matched nothing. */}
          {tools.length > 0 && shown.length === 0 && (
            <p className="tools-hint">{emptyReason()}</p>
          )}

          <div className="tools">
            {shown.map((tool) => (
              <label key={tool.name} className="tool">
                <input
                  type="checkbox"
                  checked={isOn(tool)}
                  disabled={!connected || !enabled || busy}
                  onChange={(e) => onToggleTools([tool.name], e.target.checked)}
                />
                <span className="tool-body">
                  <span className="tool-name">
                    <code>{qualifiedToolName(slug, tool.name)}</code>
                    {tool.readOnly && <span className="tag">read-only</span>}
                    {/* The storage budget cut this tool's description or argument schema
                        (never its name). It still works; enabling it and refreshing the
                        account's tools restores it -- enabled tools are trimmed last. */}
                    {tool.trimmed && (
                      <span
                        className="tag"
                        title="This server's catalog was too large to store whole, so this tool's details were shortened. It still works. Enable it and press Refresh tools to restore its full description and arguments — enabled tools keep them first."
                      >
                        details trimmed
                      </span>
                    )}
                  </span>
                  <span className="tool-desc">{tool.description}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
